import type { User, Tenant } from '../types';
import type { CookieConsentPreferences } from '../contexts/CookieConsentContext';

/**
 * Get the clarity global object (loaded via script tag)
 */
const getClarity = (): any => {
  if (typeof window !== 'undefined' && (window as any).clarity) {
    return (window as any).clarity;
  }
  return null;
};

/**
 * Load Microsoft Clarity script dynamically
 * This is the standard way to load Clarity with cookie consent
 */
const loadClarityScript = (projectId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded
    if (getClarity()) {
      resolve();
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector(`script[data-clarity-project="${projectId}"]`);
    if (existingScript) {
      // Wait for clarity API to be available
      const checkInterval = setInterval(() => {
        const clarity = getClarity();
        if (clarity && (typeof clarity.consent === 'function' || typeof clarity.consentV2 === 'function')) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        const clarity = getClarity();
        if (!clarity || (typeof clarity.consent !== 'function' && typeof clarity.consentV2 !== 'function')) {
          reject(new Error('Clarity API not available'));
        } else {
          resolve();
        }
      }, 5000);
      return;
    }

    // Load Clarity script using the standard Microsoft Clarity loader
    (function(c: any, l: Document, a: string, r: string, i: string, t: HTMLScriptElement | null, y: Node | null) {
      c[a] = c[a] || function() {
        (c[a].q = c[a].q || []).push(arguments);
      };
      t = l.createElement(r);
      t.async = true;
      t.src = "https://www.clarity.ms/tag/" + i;
      t.setAttribute('data-clarity-project', projectId);
      y = l.getElementsByTagName(r)[0];
      if (y && y.parentNode) {
        y.parentNode.insertBefore(t, y);
      } else {
        l.head.appendChild(t);
      }

      // Wait for script to load
      t.onload = () => {
        // Wait for clarity object and API methods to be available
        const checkInterval = setInterval(() => {
          const clarity = getClarity();
          // Check that clarity exists and has the methods we need
          if (clarity && (typeof clarity.consent === 'function' || typeof clarity.consentV2 === 'function')) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);

        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          const clarity = getClarity();
          if (!clarity || (typeof clarity.consent !== 'function' && typeof clarity.consentV2 !== 'function')) {
            reject(new Error('Clarity API not available after script load'));
          } else {
            resolve();
          }
        }, 5000);
      };

      t.onerror = () => {
        reject(new Error('Failed to load Clarity script'));
      };
    })(window, document, "clarity", "script", projectId, null, null);
  });
};

/**
 * Generate or retrieve a session ID
 */
const getSessionId = (): string => {
  const sessionKey = 'clarity_session_id';
  let sessionId = sessionStorage.getItem(sessionKey);
  
  if (!sessionId) {
    // Generate a unique session ID
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(sessionKey, sessionId);
  }
  
  return sessionId;
};

/**
 * Update Clarity consent based on user preferences
 * @param preferences - Cookie consent preferences
 */
export const updateClarityConsent = (preferences: CookieConsentPreferences | null): void => {
  try {
    const clarity = getClarity();
    if (!clarity) {
      console.warn('[Clarity] Clarity is not available');
      return;
    }

    if (!preferences) {
      // No consent given yet, don't call consent API
      return;
    }

    // Use v2 consent API (recommended)
    if (typeof clarity.consentV2 === 'function') {
      clarity.consentV2({
        ad_Storage: preferences.ad_Storage,
        analytics_Storage: preferences.analytics_Storage,
      });
      console.log('[Clarity] Consent updated:', preferences);
    } else if (typeof clarity.consent === 'function') {
      // Fallback to v1 API if v2 is not available
      // Only grant if analytics is granted (analytics is the main use case for Clarity)
      clarity.consent(preferences.analytics_Storage === 'granted');
      console.log('[Clarity] Consent updated (v1):', preferences.analytics_Storage === 'granted');
    }
  } catch (error) {
    console.error('[Clarity] Failed to update consent:', error);
  }
};

/**
 * Identify user in Microsoft Clarity
 * @param user - User object with email and name
 * @param tenant - Tenant/Organization object with name
 * @param pageId - Optional page identifier (e.g., route path)
 */
export const identifyClarityUser = (
  user: User | null,
  tenant: Tenant | null,
  pageId?: string
): void => {
  try {
    // Check if Clarity is available
    const clarity = getClarity();
    if (!clarity || typeof clarity.identify !== 'function') {
      console.warn('[Clarity] Clarity is not initialized');
      return;
    }

    if (!user || !user.email) {
      // No user logged in, skip identification
      return;
    }

    // Required: custom-id (using user email - Clarity hashes this securely)
    const customId = user.email;

    // Optional: custom-session-id (unique per browser session)
    const customSessionId = getSessionId();

    // Optional: custom-page-id (current route/page)
    const customPageId = pageId || window.location.pathname;

    // Optional: friendly-name (display name for the user)
    // Format: "FirstName LastName (Organization Name)" or just email if no name
    const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    const friendlyName = tenant?.name
      ? userName ? `${userName} (${tenant.name})` : `${user.email} (${tenant.name})`
      : userName || user.email;

    // Call Clarity Identify API
    // Note: Clarity securely hashes the customId on the client before sending
    clarity.identify(customId, customSessionId, customPageId, friendlyName);
    
    console.log('[Clarity] User identified:', {
      customId: customId.substring(0, 10) + '...',
      customSessionId,
      customPageId,
      friendlyName,
    });
  } catch (error) {
    console.error('[Clarity] Failed to identify user:', error);
  }
};

// Track if Clarity script has been loaded
let clarityScriptLoaded = false;

/**
 * Initialize Clarity with consent
 * Should be called after consent is given
 * This loads the Clarity script dynamically and sets up consent
 */
export const initializeClarity = async (projectId: string, preferences: CookieConsentPreferences | null): Promise<void> => {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    // Only initialize if analytics consent is granted
    if (preferences && preferences.analytics_Storage === 'granted') {
      // Check if already loaded
      if (clarityScriptLoaded && getClarity()) {
        console.log('[Clarity] Already loaded, updating consent');
        updateClarityConsent(preferences);
        return;
      }

      // Load the Clarity script
      try {
        await loadClarityScript(projectId);
        clarityScriptLoaded = true;
        console.log('[Clarity] Script loaded successfully');
        
        // Update consent after script is loaded
        updateClarityConsent(preferences);
        console.log('[Clarity] Initialized with consent');
      } catch (error) {
        console.error('[Clarity] Failed to load script:', error);
      }
    } else {
      console.log('[Clarity] Not initialized - analytics consent not granted');
    }
  } catch (error) {
    console.error('[Clarity] Failed to initialize:', error);
  }
};

